import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Elements matching FOCUSABLE_SELECTOR can still be display:none (e.g. a
// `md:hidden` mobile-only button on a desktop viewport) — focusing one of
// those is a silent no-op, so filter down to what can actually take focus.
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((el) => el.getClientRects().length > 0);
}

/**
 * Traps focus inside a modal/dialog container: moves focus in on mount,
 * cycles Tab/Shift+Tab between its first and last focusable elements so
 * focus can't escape to whatever is hidden behind the backdrop, closes on
 * Escape, and restores focus to whatever triggered the dialog on unmount.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, onClose: () => void) {
  // Read through a ref instead of putting onClose in the keydown effect's deps —
  // callers that pass an inline/non-memoized handler (the common case) would
  // otherwise cause that effect to tear down and re-add its document listener
  // on every one of the caller's re-renders (e.g. every keystroke in a search box).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const box = containerRef.current;
    const focusable = box ? getFocusable(box) : [];
    (focusable[0] ?? box)?.focus();

    return () => {
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const box = containerRef.current;
      if (!box) return;
      const focusable = getFocusable(box);
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!box.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [containerRef]);
}
